// src/components/Header.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { CTAButton, Logo, HeaderButtons } from '../styles';
import TopBar from './TopBar';

const NAV_SECTIONS = [
  { id: 'about', label: 'About' },
  { id: 'services', label: 'Services' },
  { id: 'how-it-works', label: 'How It Works' },
];

const MOBILE_PAGE_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/services', label: 'Services' },
  { to: '/man-with-van', label: 'Man with a Van' },
  { to: '/get-a-quote', label: 'Get A Free Quote' },
  { to: '/contact', label: 'Contact Us' },
];

const NavLink = ({ id, label, onClickHandler }) => (
  <a
    href={id === 'about' ? '/about' : id === 'services' ? '/services' : id === 'how-it-works' ? '/how-it-works' : '/#' + id}
    onClick={onClickHandler}
    style={{
      color: '#1a1a1a',
      textDecoration: 'none',
      fontWeight: 500,
      fontSize: '0.95rem',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </a>
);

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // USL PAGE SEO METADATA
  useEffect(() => {
    const origin = "https://urgentservicelogistics.co.uk";

    const pages = {
      "/": {
        title: "Urgent Logistics | UK-Wide Delivery & Moving Services",
        description:
          "Reliable UK-wide deliveries, house moves, furniture transport and business logistics. Get a straightforward quote from Urgent Logistics."
      },
      "/about": {
        title: "About Us | Urgent Logistics Portsmouth",
        description:
          "Learn about Urgent Logistics, based in Portsmouth with teams in London, Hampshire and Surrey. Reliable delivery and moving services across the UK."
      },
      "/services": {
        title: "Our Delivery & Moving Services | Urgent Logistics",
        description:
          "Explore our UK-wide courier, business delivery, house moving and transport services. Flexible solutions for businesses and individuals."
      },
      "/how-it-works": {
        title: "How It Works | Urgent Logistics",
        description:
          "Discover how to arrange your delivery or move with Urgent Logistics, from requesting a quote to getting your goods transported."
      },
      "/man-with-van": {
        title: "Man with a Van Services | Urgent Logistics",
        description:
          "Flexible man and van services for furniture transport, collections, deliveries and moving. Get a free quote from Urgent Logistics."
      },
      "/contact": {
        title: "Contact Us | Urgent Logistics",
        description:
          "Contact Urgent Logistics about deliveries, furniture transport, house moves and business logistics. Call 07436 117842 or send an enquiry."
      },
      "/get-a-quote": {
        title: "Get a Free Delivery or Moving Quote | Urgent Logistics",
        description:
          "Request a free quote for UK-wide deliveries, house moves, furniture transport or business logistics with Urgent Logistics."
      },
      "/quick-quote": {
        title: "Quick Quote Calculator | Urgent Logistics",
        description:
          "Use the Urgent Logistics quick quote calculator to explore delivery and transport pricing."
      }
    };

    const path = location.pathname.replace(/\/+$/, "") || "/";
    const page = pages[path];

    if (!page) return;

    document.title = page.title;

    let description = document.querySelector(
      'meta[name="description"]'
    );

    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }

    description.setAttribute("content", page.description);

    let canonical = document.querySelector(
      'link[rel="canonical"]'
    );

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute(
      "href",
      origin + (path === "/" ? "/" : path)
    );
  }, [location.pathname]);


  // USL: Lock scrolling while mobile menu is open
  useEffect(() => {
    if (!mobileOpen) return;

    const html = document.documentElement;
    const body = document.body;

    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverscroll: body.style.overscrollBehavior
    };

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';

    return () => {
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      html.style.overscrollBehavior = previous.htmlOverscroll;
      body.style.overscrollBehavior = previous.bodyOverscroll;
    };
  }, [mobileOpen]);


  const uslHeaderRef = useRef(null);

  useEffect(() => {
    const element = uslHeaderRef.current;
    if (!element) return;

    const updateHeight = () => {
      document.documentElement.style.setProperty(
        '--usl-mobile-header-height',
        `${Math.ceil(element.getBoundingClientRect().height)}px`
      );
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    window.addEventListener('resize', updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
      document.documentElement.style.removeProperty(
        '--usl-mobile-header-height'
      );
    };
  }, []);
  const isQuickQuote = location.pathname === '/quick-quote';
  const isManWithVan = location.pathname === '/man-with-van';

  // Hide the Quick Quote link on the Man with a Van page
  const mobileLinks = MOBILE_PAGE_LINKS.filter(
    (link) => !(isManWithVan && link.to === '/quick-quote')
  );

  const scrollToSection = (id) => (e) => {
    e.preventDefault();

    if (id === 'about') {
      navigate('/about');
      setMobileOpen(false);
      return;
    }

    if (id === 'services') {
      navigate('/services');
      setMobileOpen(false);
      return;
    }

    if (id === 'how-it-works') {
      navigate('/how-it-works');
      setMobileOpen(false);
      return;
    }
    if (location.pathname === '/') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  };

  return (
    <>

      <style>{`
        /* USL MOBILE PHONE AND QUOTE BAR */

        /* HOMEPAGE MOBILE HEADER: SIT ABOVE HERO */
        @media (max-width: 768px) {
          .usl-home-header-stack {
            position: sticky !important;
            top: 0;
            left: auto;
            right: auto;
          }

          /* Sticky header already occupies layout space */
          .usl-home-header-stack + .usl-mobile-header-spacer {
            display: none !important;
          }
        }


        .usl-mobile-action-bar {
          display: none;
        }

        @media (max-width: 768px) {

          /* Remove announcement bar on mobile only */
          .usl-header-stack .usl-topbar {
            display: none !important;
          }

          /* Keep navigation and buttons inside screen width */
          .usl-header-stack {
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }

          .usl-mobile-action-bar {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 6px;
            width: 100%;
            padding: 0 5% 10px;
            box-sizing: border-box;
            background: #ffffff;
          }

          .usl-mobile-phone-button,
          .usl-mobile-quote-button {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            min-width: 0;
            min-height: 44px;
            padding: 10px 5px;
            box-sizing: border-box;
            border: none;
            border-radius: 8px;
            color: #ffffff !important;
            text-decoration: none !important;
            font-size: clamp(10px, 2.7vw, 12px);
            font-weight: 700;
            text-align: center;
            white-space: nowrap;
            line-height: 1.2;
          }

          .usl-mobile-phone-button {
            background: #10243D;
          }

          .usl-mobile-quote-button {
            background: #00A8E8;
          }

          .usl-mobile-phone-button span:first-child {
            font-size: 17px;
            line-height: 1;
          }
        }
      
        .usl-mobile-header-spacer {
          display: none;
        }

        @media (max-width: 768px) {
          .usl-mobile-header-spacer {
            display: block;
            width: 100%;
            height: var(--usl-mobile-header-height, 120px);
            flex-shrink: 0;
          }
        }
`}</style>


      <style>{`
        /* USL DESKTOP HEADER CTA SIZE */
        @media (min-width: 769px) {
          .usl-desktop-contact-btn,
          .usl-desktop-quote-btn {
            padding: 0.8rem 1.35rem !important;
            font-size: 0.95rem !important;
            min-height: 0 !important;
          }

          .usl-desktop-contact-btn {
            border-width: 1px !important;
          }
        }
      `}</style>

      <style>{`
        /* USL MOBILE MENU HEADER FIX */
        @media (max-width: 768px) {
          .usl-mobile-header,
          .usl-main-header {
            box-shadow: none !important;
            border-bottom: none !important;
          }
        }
      `}</style>
      {/* Stack: TopBar sits above, header below. z-index high enough to sit above hero. */}
      <div className={location.pathname === "/" ? "usl-header-stack usl-home-header-stack" : "usl-header-stack"} ref={uslHeaderRef} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000 }}>
        <TopBar />

        <header className="usl-mobile-header"
          style={{
            background: 'white',
            padding: '1rem 5%',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div
            className="hide-on-mobile"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
          >
            <Logo as={Link} to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <img src="/urgentLogo.png" alt="Urgent Logistics Logo" style={{ height: '56px', width: 'auto' }} />
            </Logo>
          </div>

          <div className="show-on-mobile" style={{ alignItems: 'center' }}>
            <Logo as={Link} to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <img src="/urgentLogo.png" alt="Urgent Logistics Logo" style={{ height: '44px', width: 'auto' }} />
            </Logo>
          </div>

          <nav
            className="hide-on-mobile"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2rem',
              flex: '0 0 auto',
              whiteSpace: 'nowrap',
            }}
          >
            {NAV_SECTIONS.map((section) => (
              <NavLink
                key={section.id}
                id={section.id}
                label={section.label}
                onClickHandler={scrollToSection(section.id)}
              />
            ))}
            <Link
              to="/man-with-van"
              style={{
                color: isManWithVan ? '#001F3D' : '#1a1a1a',
                fontWeight: isManWithVan ? 700 : 500,
                textDecoration: 'none',
                fontSize: '0.95rem',
                whiteSpace: 'nowrap',
              }}
            >
              Man with a Van
            </Link>
          </nav>

          <div
            className="hide-on-mobile"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
          >
            <HeaderButtons>
              <CTAButton className="usl-desktop-contact-btn" as={Link} to="/contact" variant="outline" style={{ textDecoration: 'none' }}>
                Contact Us
              </CTAButton>

              {!isManWithVan && (
                <CTAButton className="usl-desktop-quote-btn"
                  as={Link}
                  to="/get-a-quote"
                  style={{
                    background: '#00A8E8',
                    border: '2px solid #00A8E8',
                    color: '#ffffff',
                    textDecoration: 'none',
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get A Free Quote
                </CTAButton>
              )}
            </HeaderButtons>
          </div>

          <button
            className="show-on-mobile"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="Toggle menu"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.5rem',
              color: '#001F3D',
              marginLeft: 'auto',
            }}
          >
            {mobileOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </header>

        {/* Mobile phone and quote buttons */}
        <div className="usl-mobile-action-bar">
          <a
            href="tel:+447436117842"
            className="usl-mobile-phone-button"
          >
            <span aria-hidden="true">☎</span>
            <span>07436 117842</span>
          </a>

          <Link
            to="/get-a-quote"
            className="usl-mobile-quote-button"
            onClick={() => setMobileOpen(false)}
          >
            GET YOUR FREE QUOTE
          </Link>
        </div>
      </div>

      <div className="usl-mobile-header-spacer" aria-hidden="true" />

      {mobileOpen && (
        <div
          className="show-on-mobile"
          style={{
            position: 'fixed',
            top: 'calc(var(--usl-mobile-header-height, 150px) - 1px)',
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            margin: 0,
            padding: '0 5% 12px',
            background: '#FFFFFF',
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            boxSizing: 'border-box',
            zIndex: 1999,
            overflow: 'hidden',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            gap: 0,
          }}
        >
          {mobileLinks.map((link) => {
            const isQuote = link.to === '/get-a-quote';
            const isContact = link.to === '/contact';
            const isButton = isQuote || isContact;
            const active = location.pathname === link.to;

            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isButton ? 'center' : 'flex-start',
                  width: '100%',
                  boxSizing: 'border-box',
                  flexShrink: 0,

                  padding: isButton
                    ? '12px 16px'
                    : '14px 0',

                  marginTop: isQuote ? '10px' : isContact ? '10px' : 0,

                  fontSize: '16px',
                  lineHeight: 1.3,
                  fontWeight: 700,
                  textDecoration: 'none',

                  color: isQuote
                    ? '#FFFFFF'
                    : active
                      ? '#00A8E8'
                      : '#10243D',

                  background: isQuote ? '#00A8E8' : '#FFFFFF',

                  border: isContact
                    ? '2px solid #10243D'
                    : 'none',

                  borderBottom: isContact
                    ? '2px solid #10243D'
                    : 'none',

                  borderRadius: isButton ? '8px' : 0,
                }}
              >
                {link.label}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
};

export default Header;
